import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/useAuth';
import api from '../services/api';
 
import { emitToast } from '../context/toast';
import { useI18n } from '../context/useI18n';
import CoachBrandingPanel from './CoachBrandingPanel';
import { fetchPaymentMethods, openWompiCheckout } from '../utils/paymentMethods';
import { buildWaMeUrl, resolvePlanSupport, resolveConsumerSupportWaUrl } from '../utils/whatsappSupport';
import { openFoodModule } from '../utils/foodModule';
import { hasPaidFoodAccess } from '../utils/foodAccess';
import AddonServicesPanel from './support/AddonServicesPanel';
import { isSedeOperatorUser, hasSedeRole, hasSedeFinalRole, isHoldingMarcaAdmin } from '../utils/gymRoleScope';
import { getUserTrainerId } from '../utils/branding';
import { isConsumerAccessBlocked, isPendingPayment, pendingPaymentMessage } from '../utils/subscriptionAccess';
import { isFinalConsumer, isCoachClientUser, isVirtualGymMember, isFoodOnlyCommercialUser } from './dashboard/userServices';
import PendingPaymentBanner from './dashboard/PendingPaymentBanner';
import { formatLocaleDate, translatePaymentMethod, translateServiceLabel, translateStatus } from '../utils/i18nHelpers';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { resizeImageFile } from '../utils/resizeImageFile';
import { Camera } from 'lucide-react';
import PhoneField from './ui/PhoneField';
import { filterPurchasableCycles, formatCycleDate, resolveCycleEnd } from '../utils/cycleUtils';

export default function MyAccount() {
  const { user, refreshProfile } = useAuth();
  const { t, lang } = useI18n();
  const isPlanSelection = (() => {
    try {
      return (window.location && window.location.pathname) === '/planes';
    } catch {
      return false;
    }
  })();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [myServices, setMyServices] = useState([]);
  const [coachInfo, setCoachInfo] = useState(null);
  const [gymInfo, setGymInfo] = useState(null);
  const [trainerPlan, setTrainerPlan] = useState(null);
  const [platformGymCode, setPlatformGymCode] = useState('');
  const [planSupport, setPlanSupport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [handledPending, setHandledPending] = useState(false);
  
  // Estados para el flujo de suscripción
  const [gyms, setGyms] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [formData, setFormData] = useState({
    gym_id: '',
    trainer_id: '',
    cycle_id: '',
    metodoPago: 'wompi_online',
    module_code: 'd28d',
  });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [profileData, setProfileData] = useState({
    telefono: user?.telefono || '',
    fecha_nacimiento: user?.fecha_nacimiento || '',
    peso: user?.peso || '',
    altura: user?.altura || '',
    objetivo: user?.objetivo || 'mantenimiento'
  });
  const [passwordData, setPasswordData] = useState({ current: '', next: '', confirm: '' });
  const [passwordMsg, setPasswordMsg] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(0);
  const avatarInputRef = useRef(null);
  const getEstadoBadgeClass = (e) => {
    if (!e) return 'badge-slate';
    const s = String(e).toLowerCase();
    if (['activo','vigente'].includes(s)) return 'badge-green';
    if (['pendiente','en_proceso'].includes(s)) return 'badge-warning';
    if (['vencido','cancelado'].includes(s)) return 'badge-danger';
    return 'badge-slate';
  };

  const rawProgram = user?.module_access?.d28d_program;
  const userProgramId = typeof rawProgram === 'string' && rawProgram.trim()
    ? rawProgram.trim()
    : null;

  const userRoleList = user?.roles?.length ? user.roles : [user?.rol].filter(Boolean);
  const isPlatformD28dStaff = userRoleList.some((r) => ['admin_d28d', 'super_admin'].includes(r));
  const isHoldingAdmin = isHoldingMarcaAdmin(user);
  const isFinalUser = isFinalConsumer(user);
  const isSedeFinalMember = hasSedeFinalRole(user) || user?.is_sede_final === true;
  const isSedeOperator = hasSedeRole(user);
  const coachClient = isCoachClientUser(user);
  const isLiveHostStaff = userRoleList.some((r) => ['entrenador_gym', 'entrenador_d28d'].includes(r))
    && !userRoleList.some((r) => ['admin_gym', 'admin_gimnasio', 'admin_marca', 'admin_d28d', 'super_admin'].includes(r));
  const [isSedeAdmin, setIsSedeAdmin] = useState(() => isSedeOperator);
  const isTrainingCoachAccount = userRoleList.some((r) =>
    ['entrenador', 'nutricionista', 'admin_training', 'admin_entrenador'].includes(r),
  );
  const profileGymId = user?.gym_id || user?.gymId || null;
  const hideProfileData = isSedeOperator || isSedeFinalMember || isSedeAdmin || isTrainingCoachAccount;
  useEffect(() => {
    if (hasSedeRole(user)) {
      setIsSedeAdmin(true);
      return undefined;
    }
    const gymId = user?.gym_id || user?.gymId;
    if (!gymId) {
      setIsSedeAdmin(false);
      return undefined;
    }
    let active = true;
    api.get(`/gyms/${gymId}`)
      .then((res) => {
        if (!active) return;
        const gym = res.data?.data || res.data || {};
        setIsSedeAdmin(isSedeOperatorUser(user, gym));
      })
      .catch(() => {
        if (active) setIsSedeAdmin(false);
      });
    return () => { active = false; };
  }, [user?.gym_id, user?.gymId, user?.roles, user?.rol]);
  const foodOnlyUser = isFoodOnlyCommercialUser(user);
  const hasD28dAccess = !foodOnlyUser && !isHoldingAdmin && Boolean(
    isSedeFinalMember
    || user?.sede_branch_active
    || user?.module_access?.d28d
    || user?.module_access?.live_classes
    || user?.module_access?.gym
    || user?.licenses?.some((l) => ['d28d', 'live_classes', 'gym'].includes(l.module_code) && l.active !== false),
  );
  const hasTrainingLicense = Boolean(
    user?.module_access?.training
    || user?.licenses?.some((l) => l.module_code === 'training' && l.active !== false),
  );
  const isD28dGymVirtualUser = hasD28dAccess && !coachClient && !hasTrainingLicense;
  const virtualGymMember = isVirtualGymMember(user);
  const cycleFieldLabel = virtualGymMember
    ? t('myaccount.cycle_gym', 'Ciclo')
    : t('myaccount.cycle_d28d', 'Ciclo Fit Live · Programa');
  // Operadores sede/gym ya no pasan por bloqueo consumidor (isConsumerAccessBlocked).
  const accessBlocked = !isSedeAdmin && !isSedeOperator && !isPlatformD28dStaff
    && isConsumerAccessBlocked(user);
  const showD28dPlans = !isHoldingAdmin && !coachClient && hasD28dAccess && !accessBlocked && !isLiveHostStaff && !isSedeAdmin;
  const showD28dSubscription = !isHoldingAdmin && !coachClient && Boolean(currentPlan) && !isLiveHostStaff && !isSedeAdmin;
  const displayServices = coachClient
    ? myServices.filter((s) => s.service !== 'd28d')
    : myServices;
  const showActiveServicesPanel = !isPlanSelection
    && displayServices.length > 0
    && !(isFinalUser && isD28dGymVirtualUser)
    && !isSedeAdmin;

  useEffect(() => {
    if (!user) return;
    setProfileData({
      telefono: user.telefono || '',
      fecha_nacimiento: user.fecha_nacimiento ? String(user.fecha_nacimiento).slice(0, 10) : '',
      peso: user.peso ?? '',
      altura: user.altura ?? '',
      objetivo: user.objetivo || 'mantenimiento',
    });
  }, [user?.id, user?.telefono, user?.fecha_nacimiento, user?.peso, user?.altura, user?.objetivo]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const planRequests = [];
      if (showD28dPlans || isPlanSelection) {
        const planParams = { kind: 'd28d', visible: 'true' };
        const userGymId = user?.gym_id ?? user?.gymId ?? null;
        if (userGymId) {
          planParams.gym_id = userGymId;
        } else if (isPlatformD28dStaff || user?.rol === 'admin_d28d' || user?.rol === 'super_admin') {
          planParams.platform_only = 'true';
        } else if (userProgramId) {
          planParams.program_id = userProgramId;
        } else {
          planParams.platform_only = 'true';
        }
        planRequests.push(api.get('/accounts/plans', { params: planParams }));
      }
      const plansResults = planRequests.length ? await Promise.all(planRequests) : [];
      const loadedPlans = plansResults.flatMap((r) => r.data || [])
        .filter((p) => p.activo !== false && p.visible !== false)
        .sort((a, b) => {
          const prog = String(a.program_id || '').localeCompare(String(b.program_id || ''));
          if (prog !== 0) return prog;
          return (a.sort_order || 0) - (b.sort_order || 0);
        });
      setPlans(loadedPlans);
      if (!isPlanSelection) {
        if (!coachClient) {
          try {
            const myAccountRes = await api.get('/accounts/me');
            const d = myAccountRes.data;
            if (d && typeof d === 'object' && ('hasAccount' in d)) {
              setCurrentPlan(d.hasAccount ? d.account : null);
              setPlanSupport(d.plan_support || null);
            } else {
              setCurrentPlan(d || null);
            }
          } catch {
            console.warn('Sin plan activo al consultar /accounts/me');
            setCurrentPlan(null);
          }
        } else {
          setCurrentPlan(null);
          setPlanSupport(null);
        }
        let resolvedServiceGym = null;
        try {
          const svcRes = await api.get('/accounts/my-services');
          setMyServices(svcRes.data?.services || []);
          setCoachInfo(svcRes.data?.coach || null);
          resolvedServiceGym = svcRes.data?.gym || null;
          setGymInfo(coachClient ? null : resolvedServiceGym);
          setTrainerPlan(svcRes.data?.trainer_plan || null);
          if (coachClient) {
            setPlatformGymCode(svcRes.data?.coach?.invite_code || user?.coach_branding?.invite_code || '');
          }
        } catch {
          setMyServices([]);
          setCoachInfo(null);
          setGymInfo(null);
          setTrainerPlan(null);
        }
        const resolvedInviteGymId = coachClient ? null : (profileGymId || resolvedServiceGym?.id || null);
        if (resolvedInviteGymId) {
          try {
            const gymRes = await api.get(`/gyms/${resolvedInviteGymId}`);
            const gym = gymRes.data?.data || gymRes.data || {};
            setPlatformGymCode(gym?.invite_code || gym?.gym_code || '');
            if (gym?.id) {
              setGymInfo((prev) => ({ ...(prev || {}), ...gym }));
            }
          } catch {
            setPlatformGymCode('');
          }
        } else if (isPlatformD28dStaff) {
          try {
            const gymsRes = await api.get('/gyms');
            const gymsList = Array.isArray(gymsRes.data) ? gymsRes.data : (gymsRes.data?.data || []);
            const anchor = gymsList.find((g) =>
              String(g.invite_code || '') === 'GYM-D28D-004'
              || String(g.gym_code || '') === 'GYM-D28D-004'
              || String(g.brand_slug || '') === 'd28d-marca-blanca',
            );
            setPlatformGymCode(anchor?.invite_code || anchor?.gym_code || '');
          } catch {
            setPlatformGymCode('');
          }
        } else {
          setPlatformGymCode('');
        }
      }
    } catch (err) {
      console.error('Error fetching account data:', err);
    } finally {
      setLoading(false);
    }
  }, [isPlanSelection, userProgramId, showD28dPlans, coachClient, isPlatformD28dStaff, profileGymId, user?.rol]);

  useEffect(() => {
    if (loading || isPlanSelection) return undefined;
    try {
      if (sessionStorage.getItem('shell_food_upsell') !== '1') return undefined;
      sessionStorage.removeItem('shell_food_upsell');
    } catch {
      return undefined;
    }
    const t = window.setTimeout(() => {
      document.getElementById('myaccount-food-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
    return () => window.clearTimeout(t);
  }, [loading, isPlanSelection]);

  const fetchOptions = useCallback(async () => {
    try {
      const cycleParams = {};
      const userGymId = user?.gym_id ?? user?.gymId ?? null;
      if (userGymId) {
        cycleParams.gym_id = userGymId;
        cycleParams.gym_only = 'true';
      } else if (isPlatformD28dStaff || user?.rol === 'admin_d28d' || user?.rol === 'super_admin') {
        cycleParams.platform_only = 'true';
      }
      const [gymsRes, trainersRes, cyclesRes] = await Promise.all([
        api.get('/gyms'),
        api.get('/trainers'),
        api.get('/cycles', { params: cycleParams }),
      ]);
      setGyms(gymsRes.data || []);
      setTrainers(trainersRes.data || []);
      setCycles(cyclesRes.data?.data || []);
    } catch (err) {
      console.error('Error cargando opciones:', err);
    }
  }, [user?.gym_id, user?.gymId, user?.rol, isPlatformD28dStaff]);

  const handleContactSupport = async () => {
    // Fuente canónica (misma que banner pendiente): gym sede / coach / plan.
    let support = null;
    try {
      const res = await api.get('/communications/support');
      support = res.data?.data || null;
      if (support) setPlanSupport(support);
    } catch {
      support = planSupport;
      if (!support) {
        const planName = currentPlan?.plan;
        const fromList = plans.find((p) => p.nombre === planName);
        support = resolvePlanSupport(fromList);
      }
    }
    const url = resolveConsumerSupportWaUrl({
      trainer: coachClient ? (coachInfo || user?.coach_branding || user?.trainer) : coachInfo,
      gym: coachClient ? null : gymInfo,
      planSupport: support,
      sedeFirst: coachClient ? false : (isSedeFinalMember || isSedeOperator || gymInfo?.parent_gym_id != null),
      user,
    });
    if (!url) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('support.no_whatsapp', 'Tu sede no tiene WhatsApp de soporte configurado.'),
      });
      return;
    }
    try {
      await api.post('/communications/whatsapp/click', {
        plan_nombre: currentPlan?.plan || null,
        program_id: user?.module_access?.d28d_program || null,
        kind: user?.module_access?.training ? 'training' : (user?.module_access?.food_plan ? 'food' : 'd28d'),
        whatsapp: support?.support_whatsapp,
        message: support?.support_message,
      });
    } catch {
      /* no bloquear apertura */
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyCoupleCode = async () => {
    const code = currentPlan?.couple_invite_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      emitToast({ type: 'success', title: t('myaccount.toast.copied', 'Copiado'), message: t('myaccount.toast.couple_copied', 'Código de pareja copiado al portapapeles') });
    } catch {
      emitToast({ type: 'info', title: code, message: t('myaccount.toast.copy_manual', 'Copia el código manualmente') });
    }
  };

  const cyclesForPlan = (plan) => {
    const ids = Array.isArray(plan?.cycle_ids) ? plan.cycle_ids : [];
    const pool = !ids.length
      ? cycles
      : cycles.filter((c) => ids.includes(Number(c.id)));
    return filterPurchasableCycles(pool);
  };

  const purchasedCycle = currentPlan?.cycle_id
    ? cycles.find((c) => Number(c.id) === Number(currentPlan.cycle_id))
    : null;
  const purchasedCycleLabel = (() => {
    if (!purchasedCycle && !currentPlan?.cycle_id) return null;
    if (!purchasedCycle) return String(currentPlan.cycle_id);
    const start = formatCycleDate(purchasedCycle.startDate || purchasedCycle.start_date);
    const end = formatCycleDate(purchasedCycle.endDate || purchasedCycle.end_date || resolveCycleEnd(purchasedCycle));
    const range = start && end ? ` (${start} – ${end})` : (start ? ` (${start})` : '');
    return `${purchasedCycle.name || `Ciclo ${purchasedCycle.id}`}${range}`;
  })();

  useEffect(() => {
    fetchData();
    if (!coachClient) fetchOptions();
    fetchPaymentMethods(coachClient || foodOnlyUser ? 'food' : 'd28d').then(setPaymentMethods);
  }, [fetchData, fetchOptions, coachClient, foodOnlyUser]);

  // Auto-suscripción si viene con plan pendiente desde el registro
  useEffect(() => {
    const tryPending = async () => {
      if (handledPending || currentPlan) return;
      let raw = null;
      try {
        raw = localStorage.getItem('pendingSubscription');
      } catch {
        console.warn('No se pudo leer pendingSubscription');
      }
      if (!raw) return;
      let pending = null;
      try {
        pending = JSON.parse(raw);
      } catch {
        console.warn('JSON inválido en pendingSubscription');
      }
      if (!pending || !pending.plan) return;
      try {
        const res = await api.post('/accounts', {
          plan: pending.plan,
          gym_id: pending.gym_id || null,
          trainer_id: pending.trainer_id || null,
          metodoPago: pending.metodoPago || 'tarjeta_credito',
        });
        setCurrentPlan(res.data?.account || null);
        emitToast({ type: 'success', title: t('myaccount.toast.sub_active', 'Suscripción activada'), message: t('myaccount.toast.plan_active', 'Plan {plan} activo', { plan: pending.plan.toUpperCase() }) });
        try { localStorage.removeItem('pendingSubscription'); } catch { console.warn('No se pudo limpiar pendingSubscription'); }
        if (isPlanSelection) { window.location.assign('/my-account'); }
      } catch (e) {
        // Si falla, no bloqueamos la vista
        console.error('Auto-suscripción falló', e);
      } finally {
        setHandledPending(true);
      }
    };
    tryPending();
  }, [handledPending, currentPlan, isPlanSelection]);

  const openSubscribeModal = (plan) => {
    setSelectedPlan(plan);
    const allowed = cyclesForPlan(plan);
    const defaultCycle = allowed.length === 1 ? String(allowed[0].id) : '';
    const planKind = plan?.kind || 'd28d';
    setShowModal(true);
    setFormData((prev) => ({
      ...prev,
      gym_id: '',
      trainer_id: coachClient ? String(getUserTrainerId(user) || '') : '',
      cycle_id: defaultCycle,
      module_code: planKind === 'food' ? 'food' : planKind === 'training' ? 'training' : 'd28d',
    }));
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    // Reglas:
    // - Si no hay gimnasio NI entrenador, el pago es obligatorio (se procesa ahora mismo).
    // - Si hay gimnasio o entrenador, el pago es opcional.

    try {
      const res = await api.post('/accounts', {
        plan: selectedPlan.nombre,
        gym_id: formData.gym_id ? parseInt(formData.gym_id, 10) : null,
        trainer_id: formData.trainer_id ? parseInt(formData.trainer_id, 10) : null,
        cycle_id: formData.cycle_id ? parseInt(formData.cycle_id, 10) : null,
        metodoPago: formData.metodoPago,
        module_code: formData.module_code || 'd28d',
      });
      const payload = res.data || {};
      if (formData.metodoPago === 'wompi_online' && payload.payment_url) {
        openWompiCheckout(payload.payment_url);
      }
      const pending = payload.pending;
      emitToast({
        type: 'success',
        title: pending ? t('myaccount.toast.pending', 'Solicitud registrada') : t('myaccount.toast.success', 'Suscripción exitosa'),
        message: pending
          ? t('myaccount.toast.pending_msg', 'Tu coach o administrador confirmará el pago en sede, o completa Wompi en la pestaña abierta.')
          : t('myaccount.toast.plan_activated', 'Plan {plan} activado', { plan: selectedPlan.nombre.toUpperCase() }),
      });
      setCurrentPlan(payload.account || null);
      await refreshProfile();
      setShowModal(false);
      if (isPlanSelection) window.location.assign('/my-account');
    } catch (err) {
      setError(err.response?.data?.error || t('myaccount.toast.subscribe_error', 'Error al suscribirse'));
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await api.put('/auth/profile', {
        telefono: profileData.telefono || null,
        fecha_nacimiento: profileData.fecha_nacimiento || null,
        peso: profileData.peso !== '' && profileData.peso != null ? parseFloat(profileData.peso) : null,
        altura: profileData.altura !== '' && profileData.altura != null ? parseFloat(profileData.altura) : null,
        objetivo: profileData.objetivo || null,
      });
      const saved = res.data?.data;
      if (saved) {
        setProfileData({
          telefono: saved.telefono || '',
          fecha_nacimiento: saved.fecha_nacimiento ? String(saved.fecha_nacimiento).slice(0, 10) : '',
          peso: saved.peso ?? '',
          altura: saved.altura ?? '',
          objetivo: saved.objetivo || 'mantenimiento',
        });
      }
      await refreshProfile();
      emitToast({ type: 'success', title: t('myaccount.toast.profile_ok', 'Perfil actualizado'), message: t('myaccount.toast.profile_saved', 'Tus datos fueron guardados') });
    } catch (err) {
      setError(err.response?.data?.error || t('myaccount.toast.profile_error', 'No se pudo actualizar el perfil'));
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      emitToast({ type: 'error', title: t('myaccount.avatar_too_large', 'Imagen demasiado grande'), message: t('myaccount.avatar_max', 'Máximo 2 MB') });
      return;
    }
    setAvatarUploading(true);
    setError('');
    try {
      const prepared = await resizeImageFile(file, { maxDim: 800, quality: 0.85 });
      const form = new FormData();
      form.append('file', prepared);
      await api.post('/auth/profile/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshProfile();
      setAvatarVersion(Date.now());
      emitToast({ type: 'success', title: t('myaccount.avatar_saved', 'Foto de perfil actualizada') });
    } catch (err) {
      emitToast({
        type: 'error',
        title: t('myaccount.avatar_error', 'No se pudo subir la foto'),
        message: err.response?.data?.error || '',
      });
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg('');
    if (passwordData.next.length < 8) {
      setPasswordMsg(t('register.password_min', 'La contraseña debe tener al menos 8 caracteres'));
      return;
    }
    if (passwordData.next !== passwordData.confirm) {
      setPasswordMsg(t('register.password_mismatch', 'Las contraseñas no coinciden'));
      return;
    }
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current,
        new_password: passwordData.next,
      });
      setPasswordData({ current: '', next: '', confirm: '' });
      emitToast({ type: 'success', title: t('users.password_updated', 'Contraseña actualizada') });
    } catch (err) {
      setPasswordMsg(err.response?.data?.error || t('users.password_update_error', 'Error actualizando contraseña'));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">{t('common.loading', 'Cargando...')}</div>;

  return (
    <div className="space-y-8">
      {accessBlocked ? (
        <PendingPaymentBanner />
      ) : null}
      {currentPlan && isPendingPayment(currentPlan.estado) ? (
        <div className="card border border-amber-300 bg-amber-50 p-4" role="status">
          <p className="text-sm text-stone-800 font-medium">{pendingPaymentMessage(t, user)}</p>
        </div>
      ) : null}
      <header className="card bg-gradient-to-br from-stone-900 to-stone-800 text-white shadow-xl overflow-hidden relative">
        <div className="relative z-10 p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-['Playfair_Display'] font-bold text-lime-400 mb-4">{t('myaccount.profile', 'Mi Perfil')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-stone-400 text-sm uppercase tracking-wider">{t('common.name', 'Nombre')}</p>
                  <p className="font-medium text-lg">{user?.nombre}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-sm uppercase tracking-wider">{t('common.email', 'Email')}</p>
                  <p className="font-medium text-lg break-all">{user?.email}</p>
                </div>
                {/* Código de invitación: solo staff/coach que invita — no usuario final / Fit Live atleta */}
                {platformGymCode && !isFinalUser && !isSedeFinalMember && !isFoodOnlyCommercialUser(user) ? (
                  <div className="md:col-span-2">
                    <p className="text-stone-400 text-sm uppercase tracking-wider mb-2">
                      {coachClient
                        ? t('myaccount.coach_code', 'Código de invitación')
                        : t('myaccount.gym_code', 'Código de invitación')}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-lime-500/40 bg-lime-500/10 p-4">
                      <code className="font-mono text-xl text-lime-300 tracking-wider">{platformGymCode}</code>
                      <button
                        type="button"
                        className="btn-secondary text-sm"
                        onClick={() => {
                          navigator.clipboard?.writeText(platformGymCode);
                          emitToast({ type: 'success', message: t('myaccount.gym_code_copied', 'Código copiado') });
                        }}
                      >
                        {t('myaccount.copy_code', 'Copiar código')}
                      </button>
                    </div>
                    <p className="text-xs text-stone-400 mt-2">
                      {coachClient
                        ? t('myaccount.coach_code_hint', 'Código de tu coach (marca blanca). Compártelo para registrar clientes de entrenamiento.')
                        : t('myaccount.gym_code_hint', 'Comparte este código cuando quieras registrar usuarios dentro del gimnasio asociado.')}
                    </p>
                  </div>
                ) : null}
                {(() => {
                  const roles = user?.roles && user.roles.length ? user.roles : [user?.rol || 'usuario_final'];
                  if (!roles.includes('super_admin')) return null;
                  return (
                    <div className="md:col-span-2 mt-2">
                      <p className="text-stone-400 text-sm uppercase tracking-wider mb-2">{t('common.roles', 'Permisos')}</p>
                      <div className="flex flex-wrap gap-2">
                        {roles.map((r) => (
                          <span key={r} className="px-3 py-1 bg-lime-500/20 text-lime-300 border border-lime-500/30 rounded-full text-xs font-semibold tracking-wide uppercase">
                            {r.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            {!isPlanSelection ? (
              <div className="flex flex-col items-center gap-2 shrink-0">
                <button
                  type="button"
                  className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-lime-500/50 bg-stone-700/50 flex items-center justify-center group"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarUploading}
                  aria-label={t('myaccount.change_avatar', 'Cambiar foto de perfil')}
                >
                  {user?.avatar_url ? (
                    <img
                      key={`${user.avatar_url}-${avatarVersion}`}
                      src={`${resolveMediaUrl(user.avatar_url)}${avatarVersion ? `?v=${avatarVersion}` : ''}`}
                      alt={user?.nombre || 'Perfil'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="text-stone-400">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  )}
                  <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition flex items-center justify-center">
                    <Camera size={22} className="text-white" aria-hidden />
                  </span>
                </button>
                <button
                  type="button"
                  className="text-xs text-lime-300 hover:text-lime-200 font-semibold"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarUploading}
                >
                  {avatarUploading
                    ? t('myaccount.avatar_uploading', 'Subiendo…')
                    : t('myaccount.change_avatar', 'Cambiar foto')}
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="hidden"
                  onChange={handleAvatarUpload}
                />
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {error && <div className="card border border-red-800 text-red-400">{error}</div>}

      {showActiveServicesPanel && (
        <section className="space-y-4">
          <h3 className="font-sans font-bold text-stone-900">{t('myaccount.active_services', 'Servicios activos')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayServices.map((svc) => (
              <div key={svc.service} className="card border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-lg font-bold text-stone-900">{translateServiceLabel(svc.service, svc.label, t)}</h4>
                  <span className={`badge ${getEstadoBadgeClass(svc.status)}`}>{translateStatus(svc.status, t)}</span>
                </div>
                {svc.service !== 'd28d' && svc.trainer?.brand_name && (
                  <p className="text-sm text-stone-600">
                    {t('myaccount.trainer', 'Entrenador')}: <strong>{svc.trainer.brand_name}</strong>
                  </p>
                )}
                {svc.service !== 'd28d' && svc.trainer?.nombre && !svc.trainer?.brand_name && (
                  <p className="text-sm text-stone-600">
                    {t('myaccount.trainer', 'Entrenador')}: <strong>{svc.trainer.nombre}</strong>
                  </p>
                )}
                {svc.service !== 'd28d' && svc.trainer?.especialidad && (
                  <p className="text-xs text-stone-500">{svc.trainer.especialidad}</p>
                )}
                {svc.program_name && (
                  <p className="text-sm text-stone-600">{t('myaccount.program', 'Programa')}: <strong>{svc.program_name}</strong></p>
                )}
                {svc.plan && (
                  <p className="text-sm text-stone-600">{t('myaccount.plan_label', 'Plan')}: <strong>{svc.plan}</strong></p>
                )}
                {svc.duration_days && (
                  <p className="text-xs text-stone-500">
                    {t('myaccount.plan_duration', 'Vigencia')}: {t('myaccount.plan_duration_days', '{days} días calendario', { days: svc.duration_days })}
                  </p>
                )}
                {svc.valid_from && (
                  <p className="text-xs text-stone-500 mt-1">{t('myaccount.start_date', 'Inicio')}: {formatLocaleDate(svc.valid_from, lang)}</p>
                )}
                {svc.valid_until && (
                  <p className="text-xs text-stone-500">{t('myaccount.expires', 'Vence')}: {formatLocaleDate(svc.valid_until, lang)}</p>
                )}
                {svc.plan_support?.support_activo !== false && (
                  <button
                    type="button"
                    className="btn-secondary text-xs mt-3 w-full"
                    onClick={async () => {
                      const url = resolveConsumerSupportWaUrl({
                        trainer: svc.trainer,
                        gym: gymInfo,
                        planSupport: svc.plan_support,
                        sedeFirst: isSedeFinalMember || isSedeOperator || gymInfo?.parent_gym_id != null,
                        user,
                      });
                      try {
                        await api.post('/communications/whatsapp/click', {
                          plan_nombre: svc.plan,
                          program_id: svc.program_id,
                          kind: svc.service,
                          whatsapp: url.match(/wa\.me\/(\d+)/)?.[1] || null,
                          message: svc.plan_support?.support_message,
                        });
                      } catch { /* noop */ }
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    {t('myaccount.whatsapp_support', 'WhatsApp soporte')}
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {!isPlanSelection && coachClient && coachInfo && (
        <section className="space-y-4">
          <h3 className="font-sans font-bold text-stone-900">{t('myaccount.my_coach', 'Mi entrenador')}</h3>
          <div className="card border border-slate-200">
            <div className="flex items-start gap-4">
              {coachInfo.logo_url ? (
                <img src={coachInfo.logo_url} alt={coachInfo.brand_name || coachInfo.nombre} className="h-14 w-14 rounded-lg object-cover" />
              ) : null}
              <div className="flex-1">
                <h4 className="text-lg font-bold text-stone-900">{coachInfo.brand_name || coachInfo.nombre}</h4>
                {coachInfo.especialidad && (
                  <p className="text-sm text-stone-600">{coachInfo.especialidad}</p>
                )}
                {coachInfo.welcome_message && (
                  <p className="text-sm text-stone-500 mt-2">{coachInfo.welcome_message}</p>
                )}
                {trainerPlan?.plan_label && (
                  <p className="text-sm text-stone-600 mt-3">
                    {t('myaccount.plan_label', 'Plan')}: <strong>{trainerPlan.plan_label}</strong>
                    {trainerPlan.months ? (
                      <span className="text-stone-500">
                        {' '}· {trainerPlan.months === 1 ? '1 mes' : `${trainerPlan.months} meses`}
                      </span>
                    ) : null}
                  </p>
                )}
                {trainerPlan?.valid_until ? (
                  <p className="text-sm text-stone-800 mt-1">
                    {(() => {
                      const end = new Date(trainerPlan.valid_until);
                      const expired = !Number.isNaN(end.getTime()) && end.getTime() < Date.now();
                      const when = formatLocaleDate(trainerPlan.valid_until, lang);
                      return expired
                        ? `Tu plan ${trainerPlan.plan_label || ''} venció el ${when}`.trim()
                        : `Tu plan ${trainerPlan.plan_label || ''} vence el ${when}`.trim();
                    })()}
                  </p>
                ) : null}
                {trainerPlan?.account_id ? (
                  <button
                    type="button"
                    className="btn-primary text-sm mt-3"
                    onClick={async () => {
                      try {
                        const r = await api.post(`/accounts/${trainerPlan.account_id}/renew`, {
                          newPlan: trainerPlan.plan_nombre || trainerPlan.plan_label,
                        });
                        const url = r.data?.payment_url;
                        if (url) window.open(url, '_blank', 'noopener,noreferrer');
                        emitToast({
                          type: 'success',
                          title: 'Renovar',
                          message: r.data?.message || 'La vigencia cambia cuando el pago se confirme.',
                        });
                      } catch (e) {
                        emitToast({
                          type: 'error',
                          title: 'Renovar',
                          message: e.response?.data?.error || 'No se pudo abrir la renovación',
                        });
                      }
                    }}
                  >
                    Renovar
                  </button>
                ) : null}
                {(trainerPlan?.valid_from || trainerPlan?.valid_until) && (
                  <p className="text-sm text-stone-600 mt-1">
                    {trainerPlan.valid_from ? (
                      <>{t('myaccount.start_date', 'Inicio')}: <strong>{formatLocaleDate(trainerPlan.valid_from, lang)}</strong></>
                    ) : null}
                    {trainerPlan.valid_from && trainerPlan.valid_until ? ' · ' : null}
                    {trainerPlan.valid_until ? (
                      <>{t('myaccount.expires', 'Vence')}: <strong>{formatLocaleDate(trainerPlan.valid_until, lang)}</strong></>
                    ) : null}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {!isPlanSelection && showD28dSubscription && (
        <section className="space-y-4">
          <h3 className="font-sans font-bold text-stone-900">{t('myaccount.current_plan', 'Plan actual')}</h3>
          {currentPlan ? (
            <div className="card">
              <h4 className="text-lg font-semibold text-stone-900 mb-2">{currentPlan.plan.toUpperCase()}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-stone-600">
                <p>{t('common.status', 'Estado')}: <span className={`badge ${getEstadoBadgeClass(currentPlan.estado)}`}>{translateStatus(currentPlan.estado, t)}</span></p>
                {purchasedCycleLabel && !isSedeFinalMember && !foodOnlyUser ? (
                  <p className="md:col-span-2">
                    <span className="font-semibold text-stone-800">{cycleFieldLabel}:</span>{' '}
                    <strong className="text-stone-900">{purchasedCycleLabel}</strong>
                    {currentPlan.cycle_deferred_until ? (
                      <span className="block text-xs text-amber-700 mt-1">
                        {t(
                          'myaccount.cycle_deferred_note',
                          'Tu acceso a clases se habilita el {date} (un día antes del inicio del ciclo).',
                          { date: formatLocaleDate(currentPlan.cycle_deferred_until || currentPlan.cycle_enable_date, lang) },
                        )}
                      </span>
                    ) : null}
                  </p>
                ) : null}
                {currentPlan.fecha_inicio ? (
                  <p>{t('myaccount.start_date', 'Inicio')}: {formatLocaleDate(currentPlan.fecha_inicio, lang)}</p>
                ) : null}
                <p>{t('myaccount.expires', 'Vence')}: {formatLocaleDate(currentPlan.fecha_vencimiento, lang)}</p>
                {foodOnlyUser ? null : (
                  <p>{t('myaccount.gym', 'Sede')}: {gyms.find(g => g.id === currentPlan.gym_id)?.nombre || 'N/A'}</p>
                )}
                {foodOnlyUser ? null : (
                  <p>{t('myaccount.sessions_left', 'Sesiones Restantes')}: {currentPlan.sesiones_restantes}</p>
                )}
              </div>
              {currentPlan.couple_invite_code && !currentPlan.couple_invite_used_by_user_id && (
                <div className="mt-4 p-4 rounded-xl bg-lime-50 border border-lime-200">
                  <p className="text-sm font-semibold text-stone-800 mb-1">{t('myaccount.couple_code_title', 'Código para tu pareja (un solo uso)')}</p>
                  <p className="font-mono text-lg text-lime-800 break-all">{currentPlan.couple_invite_code}</p>
                  <button type="button" className="btn-secondary mt-2" onClick={copyCoupleCode}>
                    {t('myaccount.copy_code', 'Copiar código')}
                  </button>
                  <p className="text-xs text-stone-500 mt-2">{t('myaccount.couple_code_hint', 'Compártelo en el registro; heredará plan, programa y vigencia.')}</p>
                </div>
              )}
              {currentPlan.primary_account_id && (
                <p className="text-sm text-stone-500 mt-3">{t('myaccount.linked_account', 'Cuenta vinculada a plan de pareja (invitado).')}</p>
              )}
            </div>
          ) : (
            <div className="card">
              <p className="text-stone-600">{t('myaccount.no_active', 'No tienes una suscripción activa.')}</p>
            </div>
          )}
        </section>
      )}

      {!isPlanSelection && isFinalUser && !isLiveHostStaff && !isSedeAdmin && !isSedeFinalMember
        && !isVirtualGymMember(user) ? (
        <section className="space-y-4" id="myaccount-food-plan">
          <h3 className="font-sans font-bold text-stone-900">
            {t('myaccount.food_plan_link', 'FOOD PLAN')}
          </h3>
          {hasPaidFoodAccess(user, myServices) ? (
            <div className="card p-4 space-y-3">
              <p className="text-sm text-stone-600">
                {t(
                  'myaccount.food_active_open_hint',
                  'Tu FOOD PLAN está activo. Ábrelo dentro del ecosistema (mismo email, SSO).',
                )}
              </p>
              <button
                type="button"
                className="btn-primary w-full"
                onClick={() => openFoodModule('/dashboard', { user, myServices })}
              >
                {t('mi_progreso.open_food', 'Ir a Food Plan')}
              </button>
            </div>
          ) : (
            <div className="card p-4">
              <AddonServicesPanel
                compact
                alwaysVisible
                excludeD28d
                excludeTraining
                allowedAddonIds={['food']}
                hideSubtitle={false}
                titleOverride={t('myaccount.food_acquire_title', 'Adquirir FOOD PLAN')}
              />
            </div>
          )}
        </section>
      ) : null}

      {(showD28dPlans || isPlanSelection) && (
      <section className="space-y-4">
        <h3 className="font-sans font-bold text-stone-900">
          {isPlanSelection ? t('myaccount.choose_plan', 'Elige tu plan') : t('myaccount.renew', 'Renovar')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.nombre}
              className={`card ${currentPlan?.plan === plan.nombre ? 'ring-2 ring-lime-400' : ''}`}
            >
              <h4 className="text-xl font-bold text-stone-900 mb-1">{(plan.display_nombre || plan.nombre).toUpperCase()}</h4>
              <p className="text-2xl font-extrabold text-lime-600 mb-2">
                ${plan.precio_mensual.toLocaleString()} COP
                {plan.precio_mensual_usd > 0 && (
                  <span className="text-base text-stone-600 font-semibold"> · USD {plan.precio_mensual_usd}</span>
                )}
                <span className="text-sm text-stone-600 font-medium"> {t('myaccount.per_month', '/ mes')}</span>
              </p>
              {plan.is_couple && (
                <p className="text-xs text-lime-700 font-medium mb-2">{t('myaccount.couple_plan', 'Plan de pareja — incluye código para 2.ª persona')}</p>
              )}
              <p className="text-stone-600 mb-4">{plan.descripcion}</p>
              <ul className="space-y-1 text-stone-600 mb-4">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="text-lime-600">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              {currentPlan?.plan === plan.nombre ? (
                <button disabled className="btn-secondary w-full opacity-80">{t('common.current', 'Tu Plan Actual')}</button>
              ) : (
                <button 
                  onClick={() => openSubscribeModal(plan)} 
                  className="btn-primary w-full"
                >
                  {t('myaccount.subscribe', 'Contratar')}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>
      )}

      {!isPlanSelection && (currentPlan || planSupport) && (
        <section className="space-y-4">
          <h3 className="font-sans font-bold text-stone-900">{t('nav.support', 'Soporte')}</h3>
          <div className="card">
            <p className="text-sm text-stone-600 mb-3">{t('support.whatsapp_hint', 'Se abrirá WhatsApp con el mensaje configurado para tu plan.')}</p>
            <button type="button" className="btn-primary" onClick={handleContactSupport}>
              {t('support.whatsapp', 'CONTACTAR SOPORTE')}
            </button>
          </div>
        </section>
      )}

      {!isPlanSelection && (user?.roles?.includes('entrenador') || user?.rol === 'entrenador') && user?.trainer_id && (
        <CoachBrandingPanel trainerId={user.trainer_id} />
      )}

      {!isPlanSelection && isFinalUser && !hideProfileData && (
        <section className="space-y-4">
          <h3 className="font-sans font-bold text-stone-900">{t('myaccount.mydata', 'Mis Datos')}</h3>
          <form onSubmit={handleSaveProfile} className="card grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <PhoneField
                label={t('common.phone', 'Teléfono')}
                value={profileData.telefono}
                onChange={(v) => setProfileData((prev) => ({ ...prev, telefono: v }))}
                inputClassName="input"
                placeholder="300 123 4567"
              />
            </div>
            <div>
              <label className="label">{t('myaccount.birthdate', 'Fecha de Nacimiento')}</label>
              <input type="date" name="fecha_nacimiento" value={profileData.fecha_nacimiento || ''} onChange={handleProfileChange} className="input" />
            </div>
            <div>
              <label className="label">{t('myaccount.objective', 'Objetivo')}</label>
              <select name="objetivo" value={profileData.objetivo || 'mantenimiento'} onChange={handleProfileChange} className="input">
                <option value="mantenimiento">{t('myaccount.objective_maintenance', 'Mantenimiento')}</option>
                <option value="perdida_grasa">{t('myaccount.objective_fatloss', 'Pérdida de Grasa')}</option>
                <option value="ganancia_muscular">{t('myaccount.objective_muscle', 'Ganancia Muscular')}</option>
              </select>
            </div>
            <div>
              <label className="label">{t('myaccount.weight', 'Peso (kg)')}</label>
              <input type="number" step="0.1" name="peso" value={profileData.peso || ''} onChange={handleProfileChange} className="input" placeholder="70" />
            </div>
            <div>
              <label className="label">{t('myaccount.height', 'Altura (cm)')}</label>
              <input type="number" step="0.1" name="altura" value={profileData.altura || ''} onChange={handleProfileChange} className="input" placeholder="175" />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button type="submit" className="btn-primary">{t('myaccount.save', 'Guardar Cambios')}</button>
            </div>
          </form>
        </section>
      )}

      {!isPlanSelection && (
        <section className="space-y-4">
          <h3 className="font-sans font-bold text-stone-900">{t('myaccount.security', 'Seguridad')}</h3>
          <form onSubmit={handleChangePassword} className="card grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            <div className="md:col-span-2">
              <label className="label">{t('myaccount.current_password', 'Contraseña actual')}</label>
              <input
                type="password"
                className="input"
                value={passwordData.current}
                onChange={(e) => setPasswordData((p) => ({ ...p, current: e.target.value }))}
                autoComplete="current-password"
                required
              />
            </div>
            <div>
              <label className="label">{t('myaccount.new_password', 'Nueva contraseña')}</label>
              <input
                type="password"
                className="input"
                value={passwordData.next}
                onChange={(e) => setPasswordData((p) => ({ ...p, next: e.target.value }))}
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <label className="label">{t('myaccount.confirm_password', 'Confirmar contraseña')}</label>
              <input
                type="password"
                className="input"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData((p) => ({ ...p, confirm: e.target.value }))}
                autoComplete="new-password"
                required
              />
            </div>
            {passwordMsg ? <p className="md:col-span-2 text-sm text-red-600">{passwordMsg}</p> : null}
            <div className="md:col-span-2">
              <button type="submit" className="btn-secondary">{t('myaccount.change_password', 'Cambiar contraseña')}</button>
            </div>
          </form>
        </section>
      )}

      {showModal && selectedPlan && (
        <div className="form-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="form-modal w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="form-modal-content">
            <h3 className="text-xl font-bold mb-1">{t('myaccount.subscribe_plan', 'Contratar Plan')} {selectedPlan.nombre.toUpperCase()}</h3>
            <p className="text-lime-400 text-2xl font-extrabold mb-4">
              ${selectedPlan.precio_mensual.toLocaleString()} COP
              {selectedPlan.precio_mensual_usd > 0 && ` · USD ${selectedPlan.precio_mensual_usd}`}
            </p>

            <form onSubmit={handleSubscribe} className="space-y-4">
              <p className="text-stone-600 text-sm">
                {t('myaccount.plan_hint_d28d', 'Elige el ciclo que adquieres. Solo se muestran ciclos vigentes o posteriores.')}
              </p>
              {cyclesForPlan(selectedPlan).length > 0 && (
                <div>
                  <label className="label">{cycleFieldLabel}</label>
                  <select
                    value={formData.cycle_id}
                    onChange={(e) => setFormData({ ...formData, cycle_id: e.target.value })}
                    className="input"
                    required={cyclesForPlan(selectedPlan).length > 0}
                  >
                    <option value="">{t('myaccount.cycle_select_ph', 'Selecciona ciclo')}</option>
                    {cyclesForPlan(selectedPlan).map((c) => (
                      <option key={c.id} value={c.id}>{c.name} ({c.startDate})</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="label">{t('myaccount.gym_optional', 'Gimnasio (opcional)')}</label>
                <select
                  value={formData.gym_id}
                  onChange={(e) => setFormData({...formData, gym_id: e.target.value})}
                  className="input"
                >
                  <option value="">{t('common.none', 'Ninguno')}</option>
                  {gyms.map(gym => (
                    <option key={gym.id} value={gym.id}>{gym.nombre}{gym.ciudad ? ` (${gym.ciudad})` : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">{t('myaccount.method', 'Método de Pago')}</label>
                <div className="space-y-2 mt-1">
                  {(paymentMethods.length ? paymentMethods : [
                    { id: 'wompi_online', label: t('myaccount.payment_wompi', 'Pago en línea (Wompi)') },
                    { id: 'pago_sede', label: t('myaccount.payment_sede', 'Pago en sede') },
                  ]).map((m) => (
                    <label key={m.id} className="flex items-center gap-2 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50">
                      <input
                        type="radio"
                        name="metodoPago"
                        value={m.id}
                        checked={formData.metodoPago === m.id}
                        onChange={() => setFormData({ ...formData, metodoPago: m.id })}
                      />
                      <span>{translatePaymentMethod(m.id, m.label, t)}</span>
                    </label>
                  ))}
                </div>
                {formData.metodoPago === 'wompi_online' && (
                  <p className="text-xs text-stone-500 mt-2">
                    {t('myaccount.wompi_hint', 'Al confirmar se abrirá el checkout Wompi en una nueva pestaña.')}
                  </p>
                )}
                {formData.metodoPago === 'pago_sede' && (
                  <p className="text-xs text-stone-500 mt-2">
                    {t('myaccount.sede_hint', 'Tu entrenador o administrador recibirá la notificación y activará tu vigencia.')}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">{t('common.cancel', 'Cancelar')}</button>
                <button type="submit" className="btn-primary">
                  {(!formData.gym_id && !formData.trainer_id) ? t('myaccount.confirm_and_pay', 'Confirmar y Pagar') : t('myaccount.confirm', 'Confirmar')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
